import type { MbcAdminGuideline } from "@nlc/shared-types";

import { useState } from "react";
import { TableBody } from "react-aria-components";

import { Badge } from "@/components/badge";
import { Link } from "@/components/link";
import { SearchField, SearchInput } from "@/components/search-field";
import { Cell, Column, Row, Table, TableHeader } from "@/components/table";

interface GuidelinesTableProps {
  readonly defaultGuidelineId: null | string;
  readonly guidelines: MbcAdminGuideline[];
}

export function GuidelinesTable({
  defaultGuidelineId,
  guidelines,
}: GuidelinesTableProps) {
  const [search, setSearch] = useState("");

  const term = search.trim().toLowerCase();
  const visibleGuidelines = guidelines
    .filter(({ name }) => name.toLowerCase().includes(term))
    .sort(
      (a, b) =>
        Number(b._id === defaultGuidelineId) -
        Number(a._id === defaultGuidelineId),
    );

  return (
    <div className="flex flex-col gap-4">
      <SearchField aria-label="Search guidelines">
        <SearchInput
          onChange={(event) => setSearch(event.target.value)}
          onClear={() => setSearch("")}
          placeholder="Search guidelines"
          value={search}
        />
      </SearchField>

      <div className="rounded-xl border border-border-secondary bg-bg-primary">
        <Table aria-label="Guidelines">
          <TableHeader>
            <Column defaultWidth="3fr" id="name" isRowHeader minWidth={200}>
              Name
            </Column>
            <Column defaultWidth="1fr" id="checks" minWidth={100}>
              Checks
            </Column>
            <Column defaultWidth="1fr" id="criteria" minWidth={100}>
              Criteria
            </Column>
          </TableHeader>
          <TableBody
            items={visibleGuidelines}
            renderEmptyState={() => (
              <p className="p-3xl text-sm text-text-tertiary">
                {guidelines.length === 0
                  ? "No guidelines yet."
                  : "No guideline matches this search."}
              </p>
            )}
          >
            {(guideline) => (
              <Row id={guideline._id}>
                <Cell>
                  <span className="flex items-center gap-sm">
                    <Link
                      className="font-medium text-text-primary"
                      href={`/admin/mbc/guidelines/${guideline._id}`}
                    >
                      {guideline.name}
                    </Link>
                    {guideline._id === defaultGuidelineId && (
                      <Badge size="small" variant="brand">
                        Default
                      </Badge>
                    )}
                  </span>
                </Cell>
                <Cell>{guideline.checkKeys.length}</Cell>
                <Cell>{guideline.criteria.length}</Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
